import clsx from 'clsx';
import set from 'lodash/set';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { getFQDNFromString } from '../../../../../../_metronic/helpers/AssetHelpers';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import UnlabeledBulletColumns from '../../../../../modules/components/list/UnlabeledBulletColumns';
import SocialMediaIcon from '../../../../../modules/components/socialMedia/SocialMediaIcon';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import {
  normalizeVpn,
  SiService,
  siteIntelligenceServiceLabel,
  Vpn,
  vpnLabel,
} from '../../../mappings';
import EditableCardContainer from '../card/EditableCardContainer';
import UrlWithMetadataCard, { UrlWithMetadataFieldType } from '../card/UrlWithMetadataCard';
import { UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { AddInOutboundLinksModal } from '../modal/AddInOutboundLinksModal';
import { HistoryModal } from '../modal/HistoryModal';
import SummaryTable, { SummaryRow } from '../table/SummaryTable';
import { getFieldUpdate, SiRequestUser } from '../utils';

interface Props {
  data: any;
  isSiAiNewData: boolean;
  reviewer?: SiRequestUser[];
  status: string;
  refetch: () => void;
  isEditable?: boolean;
  isSaving?: boolean;
  onUpdate?: UsePageDataManagerResult['updateField'];
  onSave?: UsePageDataManagerResult['saveService'];
  onReset?: UsePageDataManagerResult['resetService'];
  checkDirty?: UsePageDataManagerResult['isServiceDirty'];
}

type LinkedSite = { index?: number; _id?: string; url: string };
type UrlObj = {
  title: string;
  description: string;
  linkedSites: LinkedSite[];
};
type UrlMap = Map<string, UrlObj>;
type GroupedDetails = Map<
  Vpn,
  {
    preview: UrlMap; // one link from each of the first 5 FQDNs
    others: UrlMap; // all other links
  }
>;

export default function SocialMediaFlowDetail({
  data,
  isSiAiNewData,
  reviewer,
  status,
  refetch,
  isEditable,
  isSaving,
  onUpdate,
  onSave,
  onReset,
  checkDirty,
}: Props) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_SOCIAL_MEDIA_FLOW_DETAIL.${id}` }, values);
  const { setToast } = useToast();
  const [showVpnOtherLinks, setShowVpnOtherLinks] = useState<Set<Vpn>>(new Set());
  const [isEditing, setIsEditing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const maxPreviewLinksPerVpn = 4;
  const emDash = '—';
  const inputFieldClassName = 'form-control form-control-sm fw-normal text-dark';

  const isDirty = checkDirty && checkDirty(SiService.SOCIAL_OUTBOUND_FLOW_SERVICE);
  const totalFoundLinksFieldUpdates = getFieldUpdate('result.total_found_links', data?.field_updates);
  const socialFqdnCountFieldUpdates = getFieldUpdate('result.fqdn_group.count', data?.field_updates);
  const socialFqdnFieldUpdates = getFieldUpdate('result.fqdn_group.fqdn', data?.field_updates);
  const hasHistory = [
    totalFoundLinksFieldUpdates,
    socialFqdnCountFieldUpdates,
    socialFqdnFieldUpdates,
  ].some((fieldUpdates) => fieldUpdates.length > 0);
  const getReviewerEmail = (userId?: string) =>
    reviewer?.find((reviewerData) => reviewerData.user_id === userId)?.email ?? userId;
  const fqdnGroupReviewerNames = Array.from(
    new Set(
      [...socialFqdnCountFieldUpdates, ...socialFqdnFieldUpdates]
        .map((fieldUpdate) => getReviewerEmail(fieldUpdate.updated_by))
        .filter(Boolean)
    )
  );
  const totalFoundLinksReviewerNames = Array.from(
    new Set(
      totalFoundLinksFieldUpdates
        .map((fieldUpdate) => getReviewerEmail(fieldUpdate.updated_by))
        .filter(Boolean)
    )
  );

  const groupedDetails = useMemo(() => {
    const map: GroupedDetails = new Map();

    if (!data?.detail?.length) return map;

    const detail = data.detail

    detail.forEach((item, idx) => {
      const normalizedVpn = normalizeVpn(item?.vpn);
      if (!map.has(normalizedVpn)) {
        map.set(normalizedVpn, { preview: new Map(), others: new Map() });
      }
      const vpnObject = map.get(normalizedVpn);
      const isLinkInPreview = vpnObject.preview.has(item?.found_link);
      const isLinkInOthers = vpnObject.others.has(item?.found_link);
      const linkedSiteObj: UrlObj['linkedSites'][number] = {
        index: idx,
        _id: item?._id,
        url: item?.source_link,
      };

      if (isLinkInPreview || isLinkInOthers) {
        const existingUrlObj = vpnObject[isLinkInPreview ? 'preview' : 'others'].get(
          item?.found_link
        );
        if (!existingUrlObj.linkedSites.some((site) => site.url === item?.source_link)) {
          existingUrlObj.linkedSites.push(linkedSiteObj);
        }
      } else {
        const isPreviewMaxed = vpnObject.preview.size >= maxPreviewLinksPerVpn;
        const urlObject: UrlObj = {
          title: item?.title,
          description: item?.description,
          linkedSites: [linkedSiteObj],
        };
        if (isPreviewMaxed) {
          vpnObject.others.set(item?.found_link, urlObject);
        } else {
          vpnObject.preview.set(item?.found_link, urlObject);
        }
      }
    });
    return map;
  }, [data]);

  const handleOpenAddModal = () => {
    if (isDirty) {
      alert('You have unsaved changes. Please save before proceeding.');
    } else {
      setShowAddModal(true);
    }
  };

  const handleSave = async () => {
    await onSave(SiService.SOCIAL_OUTBOUND_FLOW_SERVICE);
    setIsEditing(false);

    // Scroll to top of card
    const element = document.getElementById('social-outbound-content');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleReset = () => {
    onReset(SiService.SOCIAL_OUTBOUND_FLOW_SERVICE);
  };

  const handleUpdateLinkData = (
    area: Vpn,
    link: string,
    linkData: UrlObj,
    field: UrlWithMetadataFieldType,
    newValue: string | string[]
  ) => {
    if (['title', 'description'].includes(field)) {
      onUpdate(SiService.SOCIAL_OUTBOUND_FLOW_SERVICE, (currentDraft) => {
        linkData.linkedSites.forEach((site) => {
          set(currentDraft, `detail[${site.index}].${field}`, newValue || null);
        });
      });
    } else if (field === 'url' && typeof newValue === 'string') {
      onUpdate(SiService.SOCIAL_OUTBOUND_FLOW_SERVICE, (currentDraft) => {
        linkData.linkedSites.forEach((site) => {
          set(currentDraft, `detail[${site.index}].found_link`, newValue || null);
        });

        // Update FQDN
        const oldFqdn = getFQDNFromString(link);
        const newFqdn = getFQDNFromString(newValue);
        if (oldFqdn !== newFqdn) {
          let removeOldFqdn = true;
          for (const item of currentDraft.detail) {
            const fqdn = getFQDNFromString(item?.found_link);
            if (fqdn === oldFqdn) {
              removeOldFqdn = false;
              break;
            }
          }

          const fqdnSet = new Set<string>([
            ...(currentDraft?.result?.fqdn_group?.fqdn ?? []),
            newFqdn,
          ]);
          if (removeOldFqdn) {
            fqdnSet.delete(oldFqdn);
          }
          set(currentDraft, 'result.fqdn_group', {
            count: fqdnSet.size,
            fqdn: Array.from(fqdnSet),
          });
        }
      });
    } else if (field === 'linkedSites' && Array.isArray(newValue)) {
      onUpdate(SiService.SOCIAL_OUTBOUND_FLOW_SERVICE, (currentDraft) => {
        const newValueCountMap = new Map<string, number>();
        newValue.forEach((url) => {
          const count = (newValueCountMap.get(url) || 0) + 1;
          newValueCountMap.set(url, count);
        });

        const linkSitesCountMap = new Map<string, number>();
        const missingLinkedSites: LinkedSite[] = [];
        linkData.linkedSites.forEach((site) => {
          const count = (linkSitesCountMap.get(site.url) || 0) + 1;
          linkSitesCountMap.set(site.url, count);
          if (count > (newValueCountMap.get(site.url) ?? 0)) {
            missingLinkedSites.push(site);
          }
        });

        const newLinkedUrlCountMap = new Map<string, number>();
        for (const url of newValue) {
          let isNew = false;
          if (!linkData.linkedSites.some((site) => site.url === url)) {
            isNew = true;
          } else if (
            newValueCountMap.get(url) - linkSitesCountMap.get(url) >
            (newLinkedUrlCountMap.get(url) ?? 0)
          ) {
            isNew = true;
            newLinkedUrlCountMap.set(url, (newLinkedUrlCountMap.get(url) ?? 0) + 1);
          }

          if (!isNew) continue;

          if (missingLinkedSites.length > 0) {
            const missingLinkedSite = missingLinkedSites.shift();
            set(currentDraft, `detail[${missingLinkedSite.index}].source_link`, url || null);
          } else {
            currentDraft.detail.push({
              found_link: link,
              source_link: url,
              title: linkData.title,
              description: linkData.description,
              vpn: area,
            });
          }
        }

        if (missingLinkedSites.length > 0) {
          currentDraft.detail = currentDraft.detail.filter(
            (item, idx) =>
              item.found_link !== link ||
              normalizeVpn(item.vpn) !== area ||
              !missingLinkedSites.some((site) => site?.index === idx)
          );
        }
      });
    }
  };

  const handleRemoveLink = (area: Vpn, link: string) => {
    onUpdate(SiService.SOCIAL_OUTBOUND_FLOW_SERVICE, (currentDraft) => {
      const newDetail = [];
      const fqdn = getFQDNFromString(link);
      let removeFqdn = true;

      for (const item of currentDraft.detail) {
        if (item.found_link !== link || normalizeVpn(item.vpn) !== area) {
          newDetail.push(item);

          if (removeFqdn && fqdn === getFQDNFromString(item?.found_link)) {
            removeFqdn = false;
          }
        }
      }
      currentDraft.detail = newDetail;

      if (removeFqdn) {
        set(currentDraft, 'result.fqdn_group', {
          count: currentDraft.result.fqdn_group.count - 1,
          fqdn: currentDraft.result.fqdn_group.fqdn.filter((f) => f !== fqdn),
        });
      }

      currentDraft.result.total_found_links = currentDraft.detail.length;
    });
  };

  const dataSection: SummaryRow[] = [
    {
      key: 'fqdnGroup',
      field: t('SOCIAL_MEDIA_FQDN', {
        count: data?.result?.fqdn_group?.count?.toLocaleString() ?? 0,
      }),
      reviewer:
        (fqdnGroupReviewerNames.length > 0 && isEditable) ? (
          <div className='d-flex align-items-center gap-1 flex-wrap'>
            {fqdnGroupReviewerNames.map((reviewerName) => (
              <Avatar key={reviewerName} name={reviewerName} />
            ))}
          </div>
        ) : undefined,
      render: () => (
        <>
          {data?.result?.fqdn_group?.fqdn?.length > 0 ? (
            <UnlabeledBulletColumns
              data={data.result.fqdn_group.fqdn}
              columnCount={data.result.fqdn_group.fqdn.length > 2 ? 2 : 1}
              columnWidth={'150px'}
              shouldBreakText
            />
          ) : (
            emDash
          )}
        </>
      ),
    },
    {
      key: 'totalFoundLinks',
      field: t('FOUND_LINKS'),
      reviewer:
        (totalFoundLinksReviewerNames.length > 0 && isEditable) ? (
          <div className='d-flex align-items-center gap-1 flex-wrap'>
            {totalFoundLinksReviewerNames.map((reviewerName) => (
              <Avatar key={reviewerName} name={reviewerName} />
            ))}
          </div>
        ) : undefined,
      render: () => {
        const value = data?.result?.total_found_links;
        return (
          <>
            {isEditing ? (
              <input
                type='number'
                className={inputFieldClassName}
                value={value ?? ''}
                onChange={(e) =>
                  onUpdate(
                    SiService.SOCIAL_OUTBOUND_FLOW_SERVICE,
                    `result.total_found_links`,
                    +e.target.value
                  )
                }
                min={0}
                step={1}
              />
            ) : (
              value ? t('ABOUT_RESULTS', { count: value.toLocaleString() }) : emDash
            )}
          </>
        );
      },
    },
  ];

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
        haveHistory={hasHistory && isEditable}
        onHistory={hasHistory ? () => setShowHistoryModal(true) : undefined}
        headerToolbar={
          isEditable && (
            <>
              {isEditing ? (
                <button
                  onClick={(e) => {
                    e.currentTarget.blur();
                    if (isDirty) {
                      handleReset();
                    }
                    setIsEditing(false);
                  }}
                  className={clsx('btn btn-sm btn-secondary')}
                  title='Cancel Edit'
                >
                  Cancel
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.currentTarget.blur();
                    setIsEditing(true);
                    setShowVpnOtherLinks(new Set(Object.values(Vpn)));
                  }}
                  className={clsx('btn btn-sm btn-primary')}
                  title='Edit'
                >
                  Edit
                </button>
              )}
              <div
                className='btn btn-icon btn-sm btn-primary'
                onClick={handleOpenAddModal}
                title='Add Social Media Links'
              >
                <i className='fas fa-plus'></i>
              </div>
            </>
          )
        }
        isEditable={isEditable && isEditing}
        isDirty={isDirty}
        isSaving={isSaving}
        onSave={handleSave}
        onReset={handleReset}
      >
        <div className='p-9 vstack gap-9'>
          <SummaryTable
            rows={dataSection}
            data={data}
            refetch={refetch}
            className='mb-0'
            title={<h5 className='fw-bolder m-0'>{t('SUMMARY')}</h5>}
          />

          {Object.keys(vpnLabel).filter((area: Vpn) => area === 'ASIA').map((area: Vpn) => {
            const groupedData = groupedDetails.get(area);
            const showOtherLinks = showVpnOtherLinks.has(area);
            return (
              <div key={area}>
                <h5 className='fw-bolder'>{t('INSPECTED_RESULTS')}</h5>

                {groupedData && groupedData.preview.size > 0 ? (
                  <div className={clsx('row row-cols-1 row-cols-lg-2', isEditing ? 'g-6' : 'g-4')}>
                    {Array.from(groupedData.preview, ([link, value], index) => (
                      <div key={`preview-${index}`}>
                        <UrlWithMetadataCard
                          url={link}
                          icon={
                            <SocialMediaIcon
                              link={link}
                              className='w-15px h-15px bg-white float-start me-2'
                            />
                          }
                          title={value?.title}
                          description={value?.description}
                          isSiAiNewData={isSiAiNewData}
                          linkedSites={isSiAiNewData ? [] : value?.linkedSites?.map((site) => site.url)}
                          isEditing={isEditing}
                          onUpdate={(field, newValue) => {
                            handleUpdateLinkData(area, link, value, field, newValue);
                          }}
                          onRemove={() => {
                            handleRemoveLink(area, link);
                          }}
                          truncateAll
                          isStatsHighlight
                          className={{ container: 'h-100' }}
                        />
                      </div>
                    ))}

                    {showOtherLinks &&
                      Array.from(groupedData.others, ([link, value], index) => (
                        <div key={`others-${index}`}>
                          <UrlWithMetadataCard
                            url={link}
                            icon={
                              <SocialMediaIcon
                                link={link}
                                className='w-15px h-15px bg-white float-start me-2'
                              />
                            }
                            title={value?.title}
                            description={value?.description}
                            isSiAiNewData={isSiAiNewData}
                            linkedSites={isSiAiNewData ? [] : value?.linkedSites?.map((site) => site.url)}
                            isEditing={isEditing}
                            onUpdate={(field, newValue) => {
                              handleUpdateLinkData(area, link, value, field, newValue);
                            }}
                            onRemove={() => {
                              handleRemoveLink(area, link);
                            }}
                            truncateAll
                            isStatsHighlight
                            className={{ container: 'h-100' }}
                          />
                        </div>
                      ))}
                  </div>
                ) : (
                  <div>{intl.formatMessage({ id: 'COMMON.NO_RECORD_FOUND' })}</div>
                )}

                {groupedData && groupedData.others.size > 0 && (
                  <div className='mt-4'>
                    <button
                      className='btn btn-link btn-color-muted btn-active-color-primary btn-sm p-0'
                      onClick={(e) => {
                        e.currentTarget.blur();
                        setShowVpnOtherLinks((prev) => {
                          const newSet = new Set(prev);
                          if (showOtherLinks) newSet.delete(area);
                          else newSet.add(area);
                          return newSet;
                        });
                      }}
                    >
                      {showOtherLinks ? (
                        <>
                          {t('SHOW_LESS')} <i className='bi bi-chevron-compact-up fs-3'></i>
                        </>
                      ) : (
                        <>
                          {t('SHOW_ALL_MORE', { remaining: groupedData.others.size })}
                          <i className='bi bi-chevron-compact-down fs-3 ms-1' />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>

      {isEditable && (
        <AddInOutboundLinksModal
          data={data}
          show={showAddModal}
          handleClose={() => setShowAddModal(false)}
          handleUpdate={onUpdate}
          handleSave={onSave}
        />
      )}

      <HistoryModal
        show={showHistoryModal}
        handleClose={() => setShowHistoryModal(false)}
        data={{ results: { users: reviewer ?? [] } }}
        service_paths={[
          {
            ref_id: data?._id,
            paths: ['result.total_found_links', 'result.fqdn_group.count', 'result.fqdn_group.fqdn'],
          },
        ]}
      />
    </>
  );
}
